import { useState, useEffect, useMemo } from 'react';
import { Container } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts/AuthContext';
import { httpGetPumps } from 'services/pumps';
import { httpGetPools } from 'services/pools';
import { httpCreateReport } from 'services/reports';
import { httpGetCompanyUsers } from 'services/users';
import { previousMonthRangeISO } from 'utils/reportDates';
import './ReportPage.scss';

const ALL = '__all__';

const REPORT_TYPE_KEYS = {
  pump_events: 'report_type_pump_events',
  pool_events: 'report_type_pool_events',
  pump_status_stats: 'report_type_status_stats',
  user_activity_log: 'report_type_user_activity',
  maintenance_history: 'report_type_maintenance',
  invoice_report: 'report_type_invoice_report',
};

const REPORT_KEYS_MAINTENANCE = ['pump_status_stats', 'maintenance_history'];
const REPORT_KEYS_MINE = ['pump_events', 'pool_events', 'user_activity_log', 'invoice_report'];

function reportKeysForRole(role) {
  const r = role == null || role === '' ? null : Number(role);
  if (r === 5 || r === 3) return REPORT_KEYS_MAINTENANCE;
  if (r === 4) return REPORT_KEYS_MINE;
  return Object.keys(REPORT_TYPE_KEYS);
}

export default function ReportPage() {
  const { t } = useTranslation();
  const { userRole } = useAuthContext();
  const [pump, setPump] = useState(ALL);
  const [locationFilter, setLocationFilter] = useState(ALL);
  const [selectedUser, setSelectedUser] = useState(ALL);
  // Reports open on the previous full calendar month.
  const [startDate, setStartDate] = useState(() => previousMonthRangeISO().start);
  const [endDate, setEndDate] = useState(() => previousMonthRangeISO().end);
  const [reportType, setReportType] = useState('pump_events');
  const [formatType, setFormatType] = useState('excel');

  const [pumps, setPumps] = useState([]);
  const [pools, setPools] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingPumps, setLoadingPumps] = useState(true);
  const [loadingPools, setLoadingPools] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [errorPumps, setErrorPumps] = useState(null);
  const [errorPools, setErrorPools] = useState(null);
  const [errorUsers, setErrorUsers] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  const reportTypeOptions = useMemo(
    () =>
      reportKeysForRole(userRole).map((value) => ({
        value,
        label: t(REPORT_TYPE_KEYS[value]),
      })),
    [t, userRole]
  );

  useEffect(() => {
    const keys = reportKeysForRole(userRole);
    if (!keys.includes(reportType)) {
      setReportType(keys[0] || 'pump_events');
    }
  }, [userRole, reportType]);

  useEffect(() => {
    let cancelled = false;

    const normalizeList = (res) =>
      Array.isArray(res.data) ? res.data : res.data?.results || [];

    (async () => {
      setLoadingPumps(true);
      setLoadingPools(true);
      setLoadingUsers(true);

      const [pumpsRes, poolsRes, usersRes] = await Promise.allSettled([
        httpGetPumps(),
        httpGetPools(),
        httpGetCompanyUsers(),
      ]);

      if (cancelled) return;

      if (pumpsRes.status === 'fulfilled') {
        setPumps(normalizeList(pumpsRes.value));
        setErrorPumps(null);
      } else {
        setPumps([]);
        setErrorPumps(t('report_err_pumps'));
      }

      if (poolsRes.status === 'fulfilled') {
        setPools(normalizeList(poolsRes.value));
        setErrorPools(null);
      } else {
        setPools([]);
        setErrorPools(t('report_err_pools'));
      }

      if (usersRes.status === 'fulfilled') {
        setUsers(normalizeList(usersRes.value));
        setErrorUsers(null);
      } else {
        setUsers([]);
        setErrorUsers(t('report_err_users'));
      }

      setLoadingPumps(false);
      setLoadingPools(false);
      setLoadingUsers(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount; avoid refetch when i18n `t` changes
  }, []);

  useEffect(() => {
    if (reportType !== 'invoice_report') return;
    const { start, end } = previousMonthRangeISO();
    setStartDate(start);
    setEndDate(end);
  }, [reportType]);

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setReportError(null);

    try {
      const reportData = {
        report_type: reportType,
        format_type: formatType,
        pump_id: pump === ALL ? null : parseInt(pump, 10),
        pool_id: locationFilter === ALL ? null : parseInt(locationFilter, 10),
        filter_user_id: selectedUser === ALL ? null : parseInt(selectedUser, 10),
        start_date: startDate,
        end_date: endDate,
      };

      const response = await httpCreateReport(reportData);

      if (response.data instanceof Blob) {
        const blob = new Blob([response.data], {
          type:
            formatType === 'csv'
              ? 'text/csv'
              : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileExtension = formatType === 'csv' ? '.csv' : '.xlsx';
        link.download = `report_${reportType}_${Date.now()}${fileExtension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        alert(
          t('report_success', {
            format: formatType.toUpperCase(),
          })
        );
      } else {
        setReportError(
          t('report_err_create', {
            detail: response.data.message || t('report_err_generic'),
          })
        );
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setReportError(t('report_err_generic'));
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <Container fluid className="dashboard-main">
      <div className="report-page">
        <div className="report-header">
          <div className="report-title">{t('report_page_title')}</div>
        </div>
        <div className="report-form">
          <div className="report-filters-grid">
            <div className="report-field">
              <div className="report-label">{t('report_type_label')}</div>
              <select
                className="report-select"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                {reportTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="report-field">
              <div className="report-label">{t('report_format_label')}</div>
              <select
                className="report-select"
                value={formatType}
                onChange={(e) => setFormatType(e.target.value)}
              >
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
              </select>
            </div>

            {reportType !== 'pool_events' && (
              <div className="report-field">
                <div className="report-label">{t('report_pumps_label')}</div>
                <select
                  className="report-select"
                  value={pump}
                  onChange={(e) => setPump(e.target.value)}
                  disabled={loadingPumps || !!errorPumps}
                >
                  <option value={ALL}>{t('filter_all')}</option>
                  {loadingPumps && <option>{t('ui_loading')}</option>}
                  {errorPumps && <option>{errorPumps}</option>}
                  {!loadingPumps &&
                    !errorPumps &&
                    pumps.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {reportType !== 'pump_events' && (
              <div className="report-field">
                <div className="report-label">{t('report_pools_label')}</div>
                <select
                  className="report-select"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  disabled={loadingPools || !!errorPools}
                >
                  <option value={ALL}>{t('filter_all')}</option>
                  {loadingPools && <option>{t('ui_loading')}</option>}
                  {errorPools && <option>{errorPools}</option>}
                  {!loadingPools &&
                    !errorPools &&
                    pools.map((pool) => (
                      <option key={pool.id} value={pool.id}>
                        {pool.name}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {reportType === 'user_activity_log' && (
              <div className="report-field">
                <div className="report-label">{t('report_user_label')}</div>
                <select
                  className="report-select"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  disabled={loadingUsers || !!errorUsers}
                >
                  <option value={ALL}>{t('report_users_all')}</option>
                  {loadingUsers && <option>{t('ui_loading')}</option>}
                  {errorUsers && <option>{errorUsers}</option>}
                  {!loadingUsers &&
                    !errorUsers &&
                    users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.username || user.email || t('report_user_fallback', { id: user.id })}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>
          <div className="report-date-row">
            <div className="report-date-group">
              <div className="report-label">{t('report_start')}</div>
              <div className="report-date-input-wrapper">
                <input
                  className="report-date-input"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span className="report-date-icon">📅</span>
              </div>
            </div>
            <div className="report-date-group">
              <div className="report-label">{t('report_end')}</div>
              <div className="report-date-input-wrapper">
                <input
                  className="report-date-input"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <span className="report-date-icon">📅</span>
              </div>
            </div>
          </div>

          {reportError && <div className="report-error">{reportError}</div>}

          <button
            className="report-generate-btn"
            type="button"
            onClick={handleGenerateReport}
            disabled={generatingReport}
          >
            {generatingReport ? t('report_generating') : t('report_download')}
          </button>
        </div>
      </div>
    </Container>
  );
}
